import { OP_STATUS, TRANS_TYPE } from './constants'

/**
 * Kebijakan resolusi op async yang gagal/terblokir di BE — terjemahan tabel keputusan
 * kontrak BE (`PosAsyncOperationPolicy`; port dari kledopos-mobile
 * `services/localdb/resolutionPolicy.ts`). Murni tanpa React: dipakai UI Pusat
 * Sinkronisasi (tombol apa yang tampil) & uploader (izin di pesan 403).
 *
 * Aksi `POST /pos/asyncOperations/{opId}/resolve`: retry_as_is | correct_payload |
 * shift_trans_date | create_product | create_contact | move_shift |
 * bypass_shift_closed | discard.
 *
 * Pilihan kasir (`choice`): `retarget` & `edit_transaction` sama-sama
 * `correct_payload` di BE — dibedakan karena UI-nya berbeda (input ID transaksi
 * server vs buka transaksi di editor kasir).
 */
export const CHOICE_ACTION = {
  retry_as_is: 'retry_as_is',
  shift_trans_date: 'shift_trans_date',
  create_product: 'create_product',
  create_contact: 'create_contact',
  retarget: 'correct_payload',
  edit_transaction: 'correct_payload',
  move_shift: 'move_shift',
  bypass_shift_closed: 'bypass_shift_closed',
  discard: 'discard',
}

/**
 * Izin tambahan untuk resolusi shift (`move_shift` / `bypass_shift_closed`) — keduanya
 * mengubah angka shift / memindah penjualan ke tally kasir lain.
 */
export const SHIFT_ADMIN_PERMISSION = 'pos_setting_cashiers_resolve_closed_shift'

const POLICY = {
  missing_product: { primary: 'create_product', alternates: ['edit_transaction', 'retry_as_is'] },
  missing_contact: { primary: 'create_contact', alternates: ['edit_transaction', 'retry_as_is'] },
  locked_trans_date: { primary: 'shift_trans_date', alternates: ['retry_as_is'] },
  target_not_found: { primary: 'retry_as_is', alternates: ['retarget'] },
  // Pindah ke shift terbuka dulu; masuk shift asal (menulis ulang shift yang sudah
  // direkonsiliasi) jadi opsi kedua yang disengaja.
  shift_closed: { primary: 'move_shift', alternates: ['bypass_shift_closed'] },
  // Duplikat: tanpa aksi utama — manusia wajib memverifikasi dulu.
  duplicate_transaction: { primary: null, alternates: ['retry_as_is', 'edit_transaction'] },
  business_rule: { primary: 'edit_transaction', alternates: ['retry_as_is'] },
  internal_error: { primary: 'retry_as_is', alternates: [] },
}

/** Error yang ikut dicoba ulang otomatis oleh "Sinkronkan Sekarang". */
export const AUTO_RETRY_ERROR_TYPES = ['internal_error', 'target_not_found']

/** Op yang butuh keputusan kasir (gagal / terblokir). */
export const needsAction = (op) =>
  op?.status === OP_STATUS.FAILED || op?.status === OP_STATUS.BLOCKED

/**
 * Kapabilitas lama (edit/hapus invoice, hapus order) sudah tak didukung BE — semua
 * aksi kecuali discard ditolak dengan pesan ini.
 */
export const isDiscardOnly = (op) => /no longer supported/i.test(op?.error ?? '')

/**
 * Pilihan resolusi untuk op: `{primary, alternates, canDiscard}`. Op yang gagal UPLOAD
 * (belum pernah diterima BE, tanpa batch_id) tak punya error_type → cukup kirim ulang
 * / batalkan lokal.
 */
export const getResolutionOptions = (op) => {
  if (!needsAction(op)) {
    return { primary: null, alternates: [], canDiscard: false }
  }
  if (isDiscardOnly(op)) {
    return { primary: null, alternates: [], canDiscard: true }
  }
  const policy = op.batch_id ? POLICY[op.error_type] : undefined
  if (!policy) {
    return { primary: 'retry_as_is', alternates: [], canDiscard: true }
  }
  return { primary: policy.primary, alternates: policy.alternates, canDiscard: true }
}

const hasPayments = (payload) => Array.isArray(payload?.payments) && payload.payments.length > 0

/**
 * Izin yang dicek BE saat op dijalankan (ulang). Menjalankan ulang op menuntut izin
 * yang sama dengan saat enqueue — payload hasil koreksi di-re-authorise, jadi
 * `payload` bisa diganti payload baru.
 */
export const opPermissions = (op, payload = op?.payload) => {
  if (op?.trans_type_id === TRANS_TYPE.INVOICE) {
    if (op.action === 'void') {
      return ['pos_invoices_void']
    }
    if (op.action === 'create' && hasPayments(payload)) {
      return ['pos_invoices_pay']
    }
    return []
  }
  if (op?.trans_type_id === TRANS_TYPE.ORDER) {
    switch (op.action) {
      case 'create':
        return ['pos_orders_add']
      case 'update':
        return ['pos_orders_edit']
      case 'pay':
        return ['pos_invoices_pay']
      case 'close':
        return ['pos_orders_close']
      default:
        return []
    }
  }
  return []
}

/** Izin yang dibutuhkan satu pilihan resolusi (op + efek sampingnya). */
export const choicePermissions = (op, choice) => {
  if (choice === 'discard') {
    return []
  }
  const base = opPermissions(op)
  if (choice === 'create_product') {
    return [...base, 'pos_setting_products_add']
  }
  if (choice === 'create_contact') {
    return [...base, 'pos_contacts_add']
  }
  if (choice === 'move_shift' || choice === 'bypass_shift_closed') {
    return [...base, SHIFT_ADMIN_PERMISSION]
  }
  return base
}
